import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { AlertTriangle, Building2, FileText, Truck } from "lucide-react";

const STORAGE_KEY = "5999-shipping-info-modal-seen-v1";

/**
 * Pop-up "Belangrijke informatie voor het verzenden van goederen".
 *
 * Two modes:
 * 1. Auto-open on first visit (uses localStorage flag).
 *    Pass `autoOpen` prop on pages where it should show automatically
 *    (offerte form, paklijst, boekingsformulier, documenten).
 *
 * 2. Manually controlled via `open`/`onClose` so the host page can ship a
 *    "Belangrijke verzendinformatie bekijken" button that reopens the modal
 *    on demand without changing the localStorage state.
 */
export default function ShippingInfoModal({ autoOpen = false, open, onClose, alwaysOpen = false }) {
  const [internalOpen, setInternalOpen] = useState(false);

  useEffect(() => {
    if (!autoOpen) return;
    try {
      if (typeof window !== "undefined" && !window.localStorage.getItem(STORAGE_KEY)) {
        // Open after small delay so the page paints first.
        const t = setTimeout(() => setInternalOpen(true), 400);
        return () => clearTimeout(t);
      }
    } catch { /* ignore storage errors */ }
  }, [autoOpen]);

  const handleClose = useCallback(() => {
    try {
      if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, "1");
    } catch { /* ignore */ }
    setInternalOpen(false);
    onClose?.();
  }, [onClose]);

  // Controlled mode (parent passes `open`) takes precedence over autoOpen.
  const isOpen = open !== undefined ? !!open : internalOpen;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="rounded-xl max-w-2xl" data-testid="shipping-info-modal">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="bg-[#1a5193]/10 p-2 mt-1">
              <AlertTriangle className="w-5 h-5 text-[#1a5193]" />
            </div>
            <DialogTitle className="text-xl font-chivo font-black text-[#082c50] leading-tight">
              Belangrijke informatie voor het verzenden van goederen
            </DialogTitle>
            <DialogDescription className="sr-only">
              Documentatie- en douanevereisten voor uw zending naar Curaçao.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-4 text-sm text-gray-700">
          <p>
            Wij hebben onze eigen fysieke locaties op de bestemmingen waardoor we efficiënter en sneller kunnen
            inklaren, mits alle documenten en informatie van uw zending compleet zijn.
          </p>
          <p className="bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900 text-sm">
            Vanwege strenge douanecontroles is correcte documentatie verplicht <strong>vóór verzending</strong>.
          </p>

          <div className="space-y-3">
            <div className="flex items-start gap-3" data-testid="shipping-info-new-goods">
              <Building2 className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wide font-bold text-[#082c50]">Nieuwe goederen</div>
                <p>Aankoopfactuur verplicht.</p>
              </div>
            </div>

            <div className="flex items-start gap-3" data-testid="shipping-info-used-goods">
              <FileText className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wide font-bold text-[#082c50]">Gebruikte goederen</div>
                <p>Volledige paklijst met geschatte dagwaarde verplicht.</p>
              </div>
            </div>

            <div className="flex items-start gap-3" data-testid="shipping-info-vehicles">
              <Truck className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wide font-bold text-[#082c50]">Motorvoertuigen</div>
                <p>
                  Factuur of waardeverklaring én een volledig ingevuld
                  <span className="font-semibold"> Vehicle Export Intake Form</span> verplicht.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Wij verzenden motorvoertuigen alleen wanneer deze aan alle voorwaarden voldoen.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-600 italic">
            Zonder volledige documentatie kunnen wij uw goederen niet verwerken of verzenden.
          </p>
        </div>

        <DialogFooter>
          {!alwaysOpen && (
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={handleClose}
              data-testid="shipping-info-later"
            >
              Later
            </Button>
          )}
          <Button
            className="rounded-xl bg-[#1a5193] hover:bg-[#194d8d] text-white"
            onClick={handleClose}
            data-testid="shipping-info-understood"
          >
            Ik begrijp het
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Helper: small inline button that opens the modal. Designed to be placed in
 *  page headers so users can reopen the info popup after the auto-open seen.
 */
export function ShippingInfoButton({ className = "", testId = "shipping-info-trigger" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[#1a5193] hover:text-[#194d8d] border-b border-[#1a5193]/40 hover:border-[#194d8d] pb-0.5 ${className}`}
        data-testid={testId}
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        Belangrijke verzendinformatie bekijken
      </button>
      <ShippingInfoModal open={open} onClose={() => setOpen(false)} alwaysOpen />
    </>
  );
}
